/**
 * ReceiveFromDeviceModal Component
 * 
 * Displays a QR code for uploading files from a mobile device.
 * User scans the QR code with their phone to access an upload page,
 * then takes photos or selects files to upload directly to the app.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import './ReceiveFromDeviceModal.css';

interface UploadedFile {
  id: string;
  key: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: Date;
}

interface ReceiveFromDeviceModalProps {
  onClose: () => void;
  onFilesReceived?: (files: UploadedFile[]) => void;
  scope?: 'global' | 'flow';
  flowId?: string | null;
}

const ReceiveFromDeviceModal: React.FC<ReceiveFromDeviceModalProps> = ({ onClose, onFilesReceived, scope = 'global', flowId }) => {
  const { user } = useAuth();
  const [sessionCode, setSessionCode] = useState<string>('');
  const [uploadUrl, setUploadUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(15 * 60); // 15 minutes in seconds
  const [receivedFiles, setReceivedFiles] = useState<UploadedFile[]>([]);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCodeRef = useRef<string>('');
  const lastFileCountRef = useRef(0);

  // Create upload session on mount
  useEffect(() => {
    createSession();

    return () => {
      // Cleanup: close session and stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (sessionCodeRef.current) {
        closeSession(sessionCodeRef.current);
      }
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (expiresIn <= 0) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }
    
    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionCode]);

  // Poll for new files — uses refs to avoid stale closure in setInterval
  const onFilesReceivedRef = useRef(onFilesReceived);
  onFilesReceivedRef.current = onFilesReceived;
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;

  const pollForFiles = useCallback(async (code: string) => {
    const userId = userIdRef.current;
    if (!userId) return;

    try {
      const response = await fetch(`/api/media/upload-session/${code}/files`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 410) {
          setError(response.status === 404 ? 'Session not found' : 'Session expired');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
        return;
      }

      const data = await response.json();
      if (data.success && data.files) {
        setReceivedFiles(data.files);

        // If new files were added, notify parent
        if (data.files.length > lastFileCountRef.current) {
          lastFileCountRef.current = data.files.length;
          if (onFilesReceivedRef.current && data.files.length > 0) {
            onFilesReceivedRef.current(data.files);
          }
        }
      }
    } catch (err) {
      console.error('[ReceiveFromDeviceModal] Poll error:', err);
    }
  }, []);

  const createSession = async () => {
    setLoading(true);
    setError(null);

    if (!user?.id) {
      setError('Please sign in to use mobile upload');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/media/upload-session/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({ scope, flowId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create upload session');
      }

      const data = await response.json();
      const code = data.code;
      setSessionCode(code);
      sessionCodeRef.current = code;

      // Generate the upload URL for mobile
      const baseUrl = window.location.origin;
      const uploadPageUrl = `${baseUrl}/upload/${code}`;
      setUploadUrl(uploadPageUrl);

      // Start polling for files
      pollIntervalRef.current = setInterval(() => {
        pollForFiles(code);
      }, 2000); // Poll every 2 seconds

      setLoading(false);
    } catch (err) {
      console.error('[ReceiveFromDeviceModal] Create session error:', err);
      setError('Failed to create upload session');
      setLoading(false);
    }
  };

  const closeSession = async (code: string) => {
    if (!user?.id) return;
    
    try {
      await fetch(`/api/media/upload-session/${code}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': user.id,
        },
      });
    } catch (err) {
      console.error('[ReceiveFromDeviceModal] Close session error:', err);
    }
  };

  const handleRefresh = async () => {
    // Close old session
    if (sessionCodeRef.current) {
      await closeSession(sessionCodeRef.current);
    }
    // Create new session
    setReceivedFiles([]);
    lastFileCountRef.current = 0;
    setExpiresIn(15 * 60);
    await createSession();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return ReactDOM.createPortal(
    <div className="receive-from-device-overlay" onClick={onClose}>
      <div className="receive-from-device-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <PhoneIphoneIcon className="header-icon" />
            <span>Receive from Device</span>
          </div>
          <button className="close-btn" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* QR Code Section */}
          <div className="qr-section">
            {loading ? (
              <div className="qr-loading">
                <div className="loading-spinner"></div>
                <span>Creating upload session...</span>
              </div>
            ) : error ? (
              <div className="qr-error">
                <span className="error-icon">⚠️</span>
                <span className="error-message">{error}</span>
                <button className="refresh-btn" onClick={handleRefresh}>
                  <RefreshIcon fontSize="small" />
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <div className="session-code">
                  <span className="code-label">Session Code:</span>
                  <span className="code-value">{sessionCode}</span>
                </div>
                
                <div className="qr-code-container">
                  <QRCodeSVG
                    value={uploadUrl}
                    size={200}
                    level="M"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                
                <div className="qr-instructions">
                  <CloudUploadIcon className="instruction-icon" />
                  <span className="instruction-text">
                    Scan with your phone to upload photos
                  </span>
                  {expiresIn > 0 ? (
                    <span className="expiry-timer">
                      Session expires in {formatTime(expiresIn)}
                    </span>
                  ) : (
                    <span className="expiry-expired">
                      Session expired
                      <button className="refresh-link" onClick={handleRefresh}>
                        Create new session
                      </button>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Received Files Section */}
          {receivedFiles.length > 0 && (
            <div className="received-files-section">
              <div className="section-header">
                <CheckCircleIcon className="success-icon" />
                <span>Received {receivedFiles.length} file{receivedFiles.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="received-files-list">
                {receivedFiles.map((file) => (
                  <div key={file.id} className="received-file-item">
                    {file.type.startsWith('image/') && (
                      <img src={file.url} alt={file.name} className="file-thumbnail" />
                    )}
                    {file.type.startsWith('video/') && (
                      <video src={file.url} className="file-thumbnail" muted />
                    )}
                    {!file.type.startsWith('image/') && !file.type.startsWith('video/') && (
                      <div className="file-icon">📄</div>
                    )}
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Refresh Button */}
          {!loading && !error && expiresIn > 0 && (
            <button className="refresh-session-btn" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
              New Session
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReceiveFromDeviceModal;
